// client/src/components/AdminCentre.jsx — manager-only admin with tabs
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import {
  Plus, Edit2, RotateCcw, UserX, UserCheck, X, Check,
  AlertCircle, Shield, Tag, Zap, GripVertical, Target, UserMinus
} from 'lucide-react';

// ── Shared helpers ────────────────────────────────────────────────────────────
const ROLES        = ['director', 'manager', 'estimator', 'bd', 'senior_viewer', 'admin_clerk', 'viewer'];
const ROLE_LABELS  = { director: 'Director', manager: 'Manager', estimator: 'Estimator', bd: 'Business Dev', senior_viewer: 'Senior Viewer', admin_clerk: 'Office Admin', viewer: 'Viewer' };
const ROLE_COLORS  = { director: '#dc2626', manager: '#10b981', estimator: '#60a5fa', bd: '#f59e0b', senior_viewer: '#a78bfa', admin_clerk: '#0e9aaa', viewer: '#64748b' };
// Type-scope options for survey-scoped users (SUR Phase 2).
const TYPE_SCOPES  = [{ value: '', label: 'All types (unscoped)' }, { value: 'surveying', label: 'Surveying only' }];

const genInitials = (name) => {
  if (!name) return '';
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 3);
};

const fmtLastLogin = (ts) => {
  if (!ts) return 'Never';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const inputStyle = {
  width: '100%', padding: '9px 12px', background: 'var(--surface2)',
  border: '1px solid var(--border)', borderRadius: 8,
  color: 'var(--text)', fontSize: 13, boxSizing: 'border-box'
};
const labelStyle = { color: 'var(--text-dim)', fontSize: 11, display: 'block', marginBottom: 5 };

// ── Shared UI primitives ──────────────────────────────────────────────────────
const Badge = ({ role }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: `${ROLE_COLORS[role]}22`, color: ROLE_COLORS[role], fontSize: 11, fontWeight: 600 }}>
    {ROLE_LABELS[role] || role}
  </span>
);

const StatusDot = ({ active }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: active ? '#10b981' : '#94a3b8' }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#10b981' : '#475569', display: 'inline-block' }} />
    {active ? 'Active' : 'Inactive'}
  </span>
);

const ActionBtn = ({ onClick, title, color = '#64748b', disabled = false, children }) => (
  <button onClick={onClick} title={title} disabled={disabled}
    style={{ background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', color: disabled ? '#334155' : color, padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'background 0.15s', opacity: disabled ? 0.4 : 1 }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
    {children}
  </button>
);

const Toast = ({ message, type = 'success', onDismiss }) => (
  <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, background: type === 'success' ? '#064e3b' : '#7f1d1d', border: `1px solid ${type === 'success' ? '#10b981' : '#f87171'}`, borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text)', fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxWidth: 360 }}>
    {type === 'success' ? <Check size={15} color="#10b981" /> : <AlertCircle size={15} color="#f87171" />}
    <span style={{ flex: 1 }}>{message}</span>
    <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 2 }}><X size={13} /></button>
  </div>
);

const ConfirmDialog = ({ label, detail, confirmLabel, danger = false, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ background: 'var(--modal-bg)', borderRadius: 12, padding: 24, width: 340, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
      <p style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>{label}</p>
      {detail && <p style={{ color: 'var(--text-dim)', fontSize: 12, margin: '0 0 20px' }}>{detail}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        <button onClick={onConfirm} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: danger ? '#dc2626' : '#3b82f6', color: '#fff' }}>{confirmLabel || 'Confirm'}</button>
      </div>
    </div>
  </div>
);

// ── OFFBOARD MODAL (REQ-08) ──────────────────────────────────────────────────
// Deactivates the leaver AND reassigns their live tenders (bidding /
// bid_review / submitted / manager_review / negotiation / paused / enquiry
// AND is_archived=false) to a chosen active estimator, in ONE atomic
// transaction on the server. Won / lost / no_bid / dead / withdrawn /
// archived rows stay with the leaver (historical attribution preserved).
// POC clocks + every date column preserved — we only move
// assigned_estimator_id.
const OffboardModal = ({ leaver, activeEstimators, onClose, onSuccess }) => {
  const [replacementId, setReplacementId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!replacementId) { setError('Choose a replacement estimator'); return; }
    setSaving(true); setError('');
    try {
      const res = await api.post(`/api/auth/users/${leaver.id}/offboard`, { replacementUserId: replacementId });
      onSuccess(
        res.transferredCount === 0
          ? `${leaver.full_name} deactivated (no live tenders to transfer).`
          : `${leaver.full_name} deactivated — ${res.transferredCount} live tender${res.transferredCount === 1 ? '' : 's'} transferred to ${res.replacement.full_name}.`
      );
    } catch (e) {
      setError(e.message || 'Offboard failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--modal-bg)', borderRadius: 14, padding: 28, width: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16 }}>Offboard Estimator</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          <strong style={{ color: 'var(--text)' }}>{leaver.full_name}</strong> ({leaver.email}) is leaving. Their live tenders will be transferred to the replacement below, then their account will be deactivated. Won / lost / archived tenders stay with them as their historical record.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Replacement Estimator</label>
          <select value={replacementId} onChange={e => setReplacementId(e.target.value)} style={inputStyle}>
            <option value="">— select an active estimator —</option>
            {activeEstimators.map(u => (
              <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
            ))}
          </select>
          {activeEstimators.length === 0 && (
            <p style={{ color: '#f87171', fontSize: 11, margin: '4px 0 0' }}>No other active estimators available — create one first.</p>
          )}
        </div>
        <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
          <p style={{ color: '#fdba74', fontSize: 11, margin: 0, lineHeight: 1.4 }}>
            <strong>Atomic action.</strong> POC clocks + every date column preserved. Audit-logged per tender. Cannot be undone without reversing manually. Teams-channel ownership transfer is a separate follow-up (R-08a).
          </p>
        </div>
        {error && <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '9px 12px', marginBottom: 16 }}><p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{error}</p></div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={saving || !replacementId || activeEstimators.length === 0}
            style={{ padding: '8px 20px', background: '#f97316', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: (saving || !replacementId) ? 'not-allowed' : 'pointer', opacity: (saving || !replacementId || activeEstimators.length === 0) ? 0.5 : 1 }}>
            {saving ? 'Offboarding…' : 'Confirm Offboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── USER MODAL ────────────────────────────────────────────────────────────────
const UserModal = ({ user, onSave, onClose }) => {
  const isEdit = !!user;
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    role: user?.role || 'estimator',
    type_scope: user?.type_scope || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!form.full_name.trim()) { setError('Full name is required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!form.email.includes('@')) { setError('Enter a valid email address'); return; }
    setSaving(true);
    // Send type_scope as null when unset (PATCH endpoint accepts null explicitly
    // to clear an existing scope; empty string is treated as no-change).
    const scopeValue = form.type_scope === '' ? null : form.type_scope;
    try {
      if (isEdit) {
        await api.patch(`/api/auth/users/${user.id}`, {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          type_scope: scopeValue,
        });
      } else {
        await api.post('/api/auth/users', {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          initials: genInitials(form.full_name),
          type_scope: scopeValue,
        });
      }
      onSave(isEdit ? `${form.full_name} updated` : `${form.full_name} created — password: ChangeMe123!`);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--modal-bg)', borderRadius: 14, padding: 28, width: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16 }}>{isEdit ? 'Edit User' : 'New User'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Full Name</label>
          <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="e.g. Laura Peters" style={inputStyle} />
          {form.full_name && !isEdit && <p style={{ color: 'var(--text-muted)', fontSize: 10, margin: '4px 0 0' }}>Initials will be: <strong style={{ color: 'var(--text-dim)' }}>{genInitials(form.full_name)}</strong></p>}
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Email Address</label>
          <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="e.g. lp@ingeniouspe.tech" type="email" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Role</label>
          {/* 7 roles won't fit one row cleanly — use flex-wrap so each button keeps a legible width. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <button key={r} onClick={() => set('role', r)}
                style={{ flex: '1 1 auto', minWidth: 100, padding: '8px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 500, cursor: 'pointer', border: form.role === r ? `1px solid ${ROLE_COLORS[r]}` : '1px solid rgba(255,255,255,0.1)', background: form.role === r ? `${ROLE_COLORS[r]}22` : 'transparent', color: form.role === r ? ROLE_COLORS[r] : '#64748b' }}>
                {ROLE_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
        {/* SUR Phase 2: type-scope selector. Applies to any role but the
            common case is Estimator + type_scope='surveying' = Survey Estimator. */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Type Scope</label>
          <select value={form.type_scope} onChange={e => set('type_scope', e.target.value)} style={inputStyle}>
            {TYPE_SCOPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p style={{ color: 'var(--text-muted)', fontSize: 10, margin: '4px 0 0' }}>
            Restricts the user to opportunities matching this project type — e.g. Estimator + Surveying = Survey-scoped Estimator (only surveying jobs, 403 on non-survey IDs).
          </p>
        </div>
        {!isEdit && (
          <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '9px 12px', marginBottom: 16 }}>
            <p style={{ color: '#6ee7b7', fontSize: 11, margin: 0 }}>Default password: <strong>ChangeMe123!</strong> — user must change on first login</p>
          </div>
        )}
        {error && <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '9px 12px', marginBottom: 16 }}><p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{error}</p></div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 20px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── REFERENCE ITEM MODAL (shared for Classifications & IDNOs) ─────────────────
const RefItemModal = ({ item, entityLabel, onSave, onClose }) => {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try { await onSave(name.trim()); }
    catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--modal-bg)', borderRadius: 14, padding: 28, width: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16 }}>{isEdit ? `Edit ${entityLabel}` : `New ${entityLabel}`}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSave()} placeholder={`Enter ${entityLabel.toLowerCase()} name`} style={inputStyle} autoFocus />
        </div>
        {error && <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '9px 12px', marginBottom: 16 }}><p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{error}</p></div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 20px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${entityLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── USERS TAB ─────────────────────────────────────────────────────────────────
const UsersTab = ({ currentUserId, showToast }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [offboardTarget, setOffboardTarget] = useState(null); // REQ-08 leaver modal

  const load = useCallback(async () => {
    try { const data = await api.get('/api/auth/users'); setUsers(data); }
    catch { showToast('Failed to load users', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (message) => { setEditUser(null); setShowCreate(false); await load(); showToast(message); };

  const handleReset = async (userId, name) => {
    try { await api.post(`/api/auth/reset-password/${userId}`, {}); showToast(`${name}'s password reset to: ChangeMe123!`); }
    catch { showToast('Reset failed', 'error'); }
  };

  const handleToggleActive = async (u) => {
    try { await api.patch(`/api/auth/users/${u.id}`, { active: u.active ? 0 : 1 }); await load(); showToast(`${u.full_name} ${u.active ? 'deactivated' : 'reactivated'}`); }
    catch { showToast('Update failed', 'error'); }
    setConfirm(null);
  };

  const active = users.filter(u => u.active);
  const inactive = users.filter(u => !u.active);

  const thStyle = { padding: '10px 14px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', borderBottom: '1px solid var(--border)' };
  const tdStyle = { padding: '12px 14px', fontSize: 13 };
  const rowStyle = (i) => ({ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' });

  const renderTable = (rows) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>User</th><th style={thStyle}>Email</th><th style={thStyle}>Role</th>
          <th style={thStyle}>Status</th><th style={thStyle}>Last Login</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((u, i) => (
          <tr key={u.id} style={rowStyle(i)}>
            <td style={tdStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.id === currentUserId ? 'rgba(16,185,129,0.2)' : `${ROLE_COLORS[u.role]}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: u.id === currentUserId ? '#10b981' : ROLE_COLORS[u.role], fontSize: 11, fontWeight: 700 }}>{u.initials}</span>
                </div>
                <p style={{ color: 'var(--text)', margin: 0, fontSize: 13, fontWeight: 500 }}>
                  {u.full_name}{u.id === currentUserId && <span style={{ color: 'var(--text-muted)', fontSize: 10, marginLeft: 6 }}>(you)</span>}
                </p>
              </div>
            </td>
            <td style={{ ...tdStyle, color: 'var(--text-dim)' }}>{u.email}</td>
            <td style={tdStyle}><Badge role={u.role} /></td>
            <td style={tdStyle}><StatusDot active={u.active} /></td>
            <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 11 }}>{fmtLastLogin(u.last_login)}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <ActionBtn onClick={() => setEditUser(u)} title="Edit user" color="#60a5fa"><Edit2 size={14} /></ActionBtn>
                <ActionBtn onClick={() => setConfirm({ action: 'reset', label: `Reset ${u.full_name}'s password?`, detail: 'Password will be reset to ChangeMe123! They will need to change it on next login.', userId: u.id, userName: u.full_name })} title="Reset password" color="#f59e0b"><RotateCcw size={14} /></ActionBtn>
                {/* REQ-08: Offboard action — only for active estimators.
                    Deactivates the user AND reassigns their live tenders to a chosen
                    replacement in one atomic transaction. */}
                {u.id !== currentUserId && u.active && u.role === 'estimator' && (
                  <ActionBtn onClick={() => setOffboardTarget(u)} title="Offboard estimator (transfer live tenders + deactivate)" color="#f97316">
                    <UserMinus size={14} />
                  </ActionBtn>
                )}
                {u.id !== currentUserId && (
                  <ActionBtn onClick={() => setConfirm({ action: u.active ? 'deactivate' : 'reactivate', label: `${u.active ? 'Deactivate' : 'Reactivate'} ${u.full_name}?`, detail: u.active ? 'The user will no longer be able to log in. Their tender history is preserved.' : 'The user will be able to log in again.', user: u })} title={u.active ? 'Deactivate' : 'Reactivate'} color={u.active ? '#f87171' : '#10b981'}>
                    {u.active ? <UserX size={14} /> : <UserCheck size={14} />}
                  </ActionBtn>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading users…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Total', value: users.length, color: '#60a5fa' },
            { label: 'Active', value: active.length, color: '#10b981' },
            { label: 'Inactive', value: inactive.length, color: 'var(--text-dim)' },
            { label: 'Directors', value: users.filter(u => u.role === 'director').length, color: '#dc2626' },
            { label: 'Managers', value: users.filter(u => u.role === 'manager').length, color: '#10b981' },
            { label: 'Estimators', value: users.filter(u => u.role === 'estimator').length, color: '#60a5fa' },
            { label: 'BD', value: users.filter(u => u.role === 'bd').length, color: '#f59e0b' },
            { label: 'Office Admin', value: users.filter(u => u.role === 'admin_clerk').length, color: '#0e9aaa' },
            { label: 'Senior Viewer', value: users.filter(u => u.role === 'senior_viewer').length, color: '#a78bfa' },
            { label: 'Viewer', value: users.filter(u => u.role === 'viewer').length, color: '#64748b' },
            { label: 'Survey scope', value: users.filter(u => u.type_scope === 'surveying').length, color: '#0e9aaa' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)', minWidth: 64 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 3px' }}>{s.label}</p>
              <p style={{ color: s.color, fontSize: 20, fontWeight: 700, margin: 0 }}>{s.value}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, flexShrink: 0 }}>
          <Plus size={13} /> New User
        </button>
      </div>
      <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 14 }}>Active Users ({active.length})</h3>
        </div>
        {active.length === 0 ? <p style={{ color: 'var(--text-muted)', padding: 24, margin: 0 }}>No active users</p> : renderTable(active)}
      </div>
      {inactive.length > 0 && (
        <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', opacity: 0.8 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ color: 'var(--text-dim)', margin: 0, fontSize: 14 }}>Inactive Users ({inactive.length})</h3>
          </div>
          {renderTable(inactive)}
        </div>
      )}
      {showCreate && <UserModal onSave={handleSave} onClose={() => setShowCreate(false)} />}
      {editUser && <UserModal user={editUser} onSave={handleSave} onClose={() => setEditUser(null)} />}
      {offboardTarget && (
        <OffboardModal
          leaver={offboardTarget}
          activeEstimators={users.filter(u => u.role === 'estimator' && u.active && u.id !== offboardTarget.id)}
          onClose={() => setOffboardTarget(null)}
          onSuccess={async (msg) => { setOffboardTarget(null); await load(); showToast(msg); }}
        />
      )}
      {confirm && (
        <ConfirmDialog label={confirm.label} detail={confirm.detail}
          confirmLabel={confirm.action === 'deactivate' ? 'Deactivate' : confirm.action === 'reset' ? 'Reset' : 'Reactivate'}
          danger={confirm.action === 'deactivate'}
          onConfirm={() => { if (confirm.action === 'reset') handleReset(confirm.userId, confirm.userName); else handleToggleActive(confirm.user); }}
          onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
};

// ── CLASSIFICATIONS TAB ───────────────────────────────────────────────────────
const ClassificationsTab = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragNodeRef = useRef(null);

  const load = useCallback(async () => {
    try { const data = await api.get('/api/reference/classifications/all'); setItems(data); }
    catch { showToast('Failed to load classifications', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (name) => {
    await api.post('/api/reference/classifications', { name });
    await load(); setShowCreate(false); showToast(`"${name}" added`);
  };

  const handleEdit = async (name) => {
    await api.patch(`/api/reference/classifications/${editItem.id}`, { name });
    await load(); setEditItem(null); showToast(`"${name}" updated`);
  };

  const handleToggleActive = async (item) => {
    if (item.active) {
      const { inUse, count } = await api.get(`/api/reference/classifications/${item.id}/in-use`);
      if (inUse) {
        setConfirm({ item, label: `Deactivate "${item.name}"?`, detail: `This classification is assigned to ${count} tender${count !== 1 ? 's' : ''}. It will be hidden from dropdowns but those tenders will retain their classification.` });
        return;
      }
    }
    await api.patch(`/api/reference/classifications/${item.id}`, { active: item.active ? 0 : 1 });
    await load(); showToast(`"${item.name}" ${item.active ? 'deactivated' : 'reactivated'}`);
  };

  const activeItems   = items.filter(i => i.active);
  const inactiveItems = items.filter(i => !i.active);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.4'; }, 0);
  };
  const handleDragEnter = (idx) => { if (idx !== dragIdx) setDragOverIdx(idx); };
  const handleDragEnd   = () => { if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1'; setDragIdx(null); setDragOverIdx(null); };
  const handleDrop      = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    const reordered = [...activeItems];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setItems([...reordered, ...inactiveItems]);
    const order = reordered.map((item, i) => ({ id: item.id, sort_order: i }));
    try { await api.patch('/api/reference/classifications/reorder', { order }); showToast('Order saved'); }
    catch { showToast('Failed to save order', 'error'); load(); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  const thStyle = { padding: '10px 14px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', borderBottom: '1px solid var(--border)' };
  const tdStyle = { padding: '11px 14px', fontSize: 13 };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {[{ label: 'Total', value: items.length, color: '#60a5fa' }, { label: 'Active', value: activeItems.length, color: '#10b981' }, { label: 'Inactive', value: inactiveItems.length, color: 'var(--text-dim)' }].map(s => (
            <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)', minWidth: 64 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 3px' }}>{s.label}</p>
              <p style={{ color: s.color, fontSize: 20, fontWeight: 700, margin: 0 }}>{s.value}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <Plus size={13} /> New Classification
        </button>
      </div>

      <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 14 }}>Active ({activeItems.length})</h3>
          <span style={{ color: 'var(--text-muted)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><GripVertical size={11} /> Drag to reorder</span>
        </div>
        {activeItems.length === 0 ? <p style={{ color: 'var(--text-muted)', padding: 24, margin: 0 }}>No active classifications</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...thStyle, width: 36, paddingRight: 0 }}></th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr></thead>
            <tbody>
              {activeItems.map((item, idx) => (
                <tr key={item.id} draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd}
                  onDrop={() => handleDrop(idx)}
                  onDragOver={e => e.preventDefault()}
                  style={{ borderBottom: '1px solid var(--border)', background: dragOverIdx === idx ? 'rgba(59,130,246,0.1)' : idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)', cursor: 'grab', transition: 'background 0.1s', outline: dragOverIdx === idx ? '1px solid rgba(59,130,246,0.4)' : 'none' }}>
                  <td style={{ ...tdStyle, width: 36, paddingRight: 0, color: '#334155' }}><GripVertical size={14} /></td>
                  <td style={{ ...tdStyle, color: 'var(--text)', fontWeight: 500 }}>{item.name}</td>
                  <td style={tdStyle}><StatusDot active={true} /></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <ActionBtn onClick={() => setEditItem(item)} title="Edit" color="#60a5fa"><Edit2 size={14} /></ActionBtn>
                      <ActionBtn onClick={() => handleToggleActive(item)} title="Deactivate" color="#f87171"><UserX size={14} /></ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inactiveItems.length > 0 && (
        <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', opacity: 0.8 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ color: 'var(--text-dim)', margin: 0, fontSize: 14 }}>Inactive ({inactiveItems.length})</h3>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Status</th><th style={{ ...thStyle, textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {inactiveItems.map((item, idx) => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{item.name}</td>
                  <td style={tdStyle}><StatusDot active={false} /></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <ActionBtn onClick={() => setEditItem(item)} title="Edit" color="#60a5fa"><Edit2 size={14} /></ActionBtn>
                      <ActionBtn onClick={() => handleToggleActive(item)} title="Reactivate" color="#10b981"><UserCheck size={14} /></ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <RefItemModal entityLabel="Classification" onSave={handleCreate} onClose={() => setShowCreate(false)} />}
      {editItem && <RefItemModal item={editItem} entityLabel="Classification" onSave={handleEdit} onClose={() => setEditItem(null)} />}
      {confirm && <ConfirmDialog label={confirm.label} detail={confirm.detail} confirmLabel="Deactivate" danger
        onConfirm={async () => { await api.patch(`/api/reference/classifications/${confirm.item.id}`, { active: 0 }); await load(); showToast(`"${confirm.item.name}" deactivated`); setConfirm(null); }}
        onCancel={() => setConfirm(null)} />}
    </div>
  );
};

// ── IDNOs TAB ─────────────────────────────────────────────────────────────────
const IdnosTab = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { const data = await api.get('/api/reference/idnos/all'); setItems(data); }
    catch { showToast('Failed to load IDNOs', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (name) => {
    await api.post('/api/reference/idnos', { name });
    await load(); setShowCreate(false); showToast(`"${name}" added`);
  };

  const handleEdit = async (name) => {
    await api.patch(`/api/reference/idnos/${editItem.id}`, { name });
    await load(); setEditItem(null); showToast(`"${name}" updated`);
  };

  const handleToggleActive = async (item) => {
    if (item.active) {
      const { inUse, count } = await api.get(`/api/reference/idnos/${item.id}/in-use`);
      if (inUse) {
        setConfirm({ item, label: `Deactivate "${item.name}"?`, detail: `This IDNO is assigned to ${count} tender${count !== 1 ? 's' : ''}. It will be hidden from dropdowns but those tenders will retain their IDNO assignment.` });
        return;
      }
    }
    await api.patch(`/api/reference/idnos/${item.id}`, { active: item.active ? 0 : 1 });
    await load(); showToast(`"${item.name}" ${item.active ? 'deactivated' : 'reactivated'}`);
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  const activeItems   = items.filter(i => i.active);
  const inactiveItems = items.filter(i => !i.active);
  const thStyle = { padding: '10px 14px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', borderBottom: '1px solid var(--border)' };
  const tdStyle = { padding: '11px 14px', fontSize: 13 };

  const renderTable = (rows, inactive = false) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Status</th><th style={{ ...thStyle, textAlign: 'right' }}>Actions</th></tr></thead>
      <tbody>
        {rows.map((item, idx) => (
          <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
            <td style={{ ...tdStyle, color: inactive ? '#64748b' : '#f1f5f9', fontWeight: 500 }}>{item.name}</td>
            <td style={tdStyle}><StatusDot active={item.active} /></td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <ActionBtn onClick={() => setEditItem(item)} title="Edit" color="#60a5fa"><Edit2 size={14} /></ActionBtn>
                <ActionBtn onClick={() => handleToggleActive(item)} title={item.active ? 'Deactivate' : 'Reactivate'} color={item.active ? '#f87171' : '#10b981'}>
                  {item.active ? <UserX size={14} /> : <UserCheck size={14} />}
                </ActionBtn>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {[{ label: 'Total', value: items.length, color: '#60a5fa' }, { label: 'Active', value: activeItems.length, color: '#10b981' }, { label: 'Inactive', value: inactiveItems.length, color: 'var(--text-dim)' }].map(s => (
            <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)', minWidth: 64 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 3px' }}>{s.label}</p>
              <p style={{ color: s.color, fontSize: 20, fontWeight: 700, margin: 0 }}>{s.value}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <Plus size={13} /> New IDNO
        </button>
      </div>
      <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 14 }}>Active ({activeItems.length})</h3>
        </div>
        {activeItems.length === 0 ? <p style={{ color: 'var(--text-muted)', padding: 24, margin: 0 }}>No active IDNOs</p> : renderTable(activeItems)}
      </div>
      {inactiveItems.length > 0 && (
        <div style={{ background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', opacity: 0.8 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ color: 'var(--text-dim)', margin: 0, fontSize: 14 }}>Inactive ({inactiveItems.length})</h3>
          </div>
          {renderTable(inactiveItems, true)}
        </div>
      )}
      {showCreate && <RefItemModal entityLabel="IDNO" onSave={handleCreate} onClose={() => setShowCreate(false)} />}
      {editItem && <RefItemModal item={editItem} entityLabel="IDNO" onSave={handleEdit} onClose={() => setEditItem(null)} />}
      {confirm && <ConfirmDialog label={confirm.label} detail={confirm.detail} confirmLabel="Deactivate" danger
        onConfirm={async () => { await api.patch(`/api/reference/idnos/${confirm.item.id}`, { active: 0 }); await load(); showToast(`"${confirm.item.name}" deactivated`); setConfirm(null); }}
        onCancel={() => setConfirm(null)} />}
    </div>
  );
};

// ── CLASSIFICATION TARGETS TAB ────────────────────────────────────────────────
const ClassificationTargetsTab = ({ showToast }) => {
  const [targets, setTargets] = useState([]);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getClassificationTargets().then(data => {
      setTargets(data);
      const init = {};
      data.forEach(r => { init[r.classification_name] = r.target_working_days; });
      setEdited(init);
    }).catch(() => showToast('Failed to load targets', 'error'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = targets.map(r => ({
        classification_name: r.classification_name,
        target_working_days: parseInt(edited[r.classification_name]) || r.target_working_days,
      }));
      await api.updateClassificationTargets(payload);
      showToast('Targets saved');
    } catch {
      showToast('Failed to save targets', 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = targets.some(r =>
    parseInt(edited[r.classification_name]) !== r.target_working_days
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <h3 style={{ color:'var(--text)', margin:'0 0 4px', fontSize:14 }}>Estimator Target Days by Classification</h3>
          <p style={{ color:'var(--text-muted)', fontSize:12, margin:0 }}>Working days from POC received (or tender receipt if POC not required) to submission. Used to measure estimator performance.</p>
        </div>
        {hasChanges && (
          <button onClick={handleSave} disabled={saving}
            style={{ padding:'8px 16px', background:'var(--green)', border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {targets.map(r => (
          <div key={r.classification_name} style={{ display:'flex', alignItems:'center', gap:12, background:'var(--surface2)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
            <span style={{ color:'var(--text)', fontSize:13, flex:1 }}>{r.classification_name}</span>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input
                type="number" min="1" max="60"
                value={edited[r.classification_name] ?? r.target_working_days}
                onChange={e => setEdited(prev => ({ ...prev, [r.classification_name]: e.target.value }))}
                style={{ width:60, padding:'5px 8px', background:'var(--card-bg)', border:`1px solid ${parseInt(edited[r.classification_name]) !== r.target_working_days ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius:6, color:'var(--text)', fontSize:13, textAlign:'center' }}
              />
              <span style={{ color:'var(--text-muted)', fontSize:12 }}>working days</span>
            </div>
          </div>
        ))}
      </div>
      <p style={{ color:'var(--text-muted)', fontSize:11, marginTop:14 }}>Changes take effect immediately for all active tenders. Historical data is unaffected.</p>
    </div>
  );
};

// ── ROOT AdminCentre ──────────────────────────────────────────────────────────
const TABS = [
  { key: 'users',           label: 'Users',          icon: Shield },
  { key: 'classifications', label: 'Classifications', icon: Tag    },
  { key: 'idnos',           label: 'IDNOs',           icon: Zap    },
  { key: 'targets',         label: 'Est. Targets',    icon: Target },
];

export default function AdminCentre({ currentUserId }) {
  const [activeTab, setActiveTab] = useState('users');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <Shield size={20} color="#10b981" />
        <h2 style={{ color: 'var(--text)', margin: 0, fontSize: 18 }}>Admin Centre</h2>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(({ key, label, icon: Icon }) => {
          const isActive = activeTab === key;
          return (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{ padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--teal)' : 'var(--text-muted)', borderBottom: isActive ? '2px solid var(--teal)' : '2px solid transparent', marginBottom: -1, transition: 'color 0.15s' }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--text-muted)'; }}>
              <Icon size={14} />{label}
            </button>
          );
        })}
      </div>

      {activeTab === 'users'           && <UsersTab currentUserId={currentUserId} showToast={showToast} />}
      {activeTab === 'classifications' && <ClassificationsTab showToast={showToast} />}
      {activeTab === 'idnos'           && <IdnosTab showToast={showToast} />}
      {activeTab === 'targets'         && <ClassificationTargetsTab showToast={showToast} />}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
